import {
  API,
  deleteRequest,
  getRequest,
  getRequestBlob,
  patchRequest,
  postRequest,
  putRequest,
} from '@/api/api';
import { generateSearchParams } from '@/utils/generate-search-params';

import type {
  CreateSafetyLogFormValues,
  SafetyLog,
  SafetyLogResponseDto,
  SafetyLogsList,
  SafetyLogsListDto,
  UpdateSafetyLogFormValues,
} from './safety-log.dto';
import { mapCreateSafetyLogDto, mapSafetyLog, mapUpdateSafetyLogDto } from './safety-log.mappers';

export const getSafetyLogs = async (limit: number, page: number): Promise<SafetyLogsList> => {
  const searchParams = generateSearchParams({ limit, page });
  const { data } = await getRequest<SafetyLogsListDto>(API.VESSEL, `/safety-log?${searchParams}`);
  return {
    count: data.count,
    items: data.items.map(mapSafetyLog),
  };
};

export const getSafetyLogById = async (id: string): Promise<SafetyLog> => {
  const { data } = await getRequest<SafetyLogResponseDto>(API.VESSEL, `/safety-log/${id}`);
  return mapSafetyLog(data);
};

const toRfc3339 = (date: string): string => {
  const d = new Date(date.length === 10 ? `${date}T00:00:00.000Z` : date);
  return Number.isNaN(d.getTime()) ? date : d.toISOString();
};

export const getSafetyLogsByDate = async (date: string): Promise<SafetyLog[]> => {
  const { data } = await getRequest<SafetyLogResponseDto[] | undefined>(
    API.VESSEL,
    `/safety-log/date`,
    false,
    { date: toRfc3339(date) },
  );
  return (data ?? []).map(mapSafetyLog);
};

export const createSafetyLog = async (values: CreateSafetyLogFormValues): Promise<SafetyLog> => {
  const { data } = await postRequest<SafetyLogResponseDto, unknown>(
    API.VESSEL,
    `/safety-log`,
    mapCreateSafetyLogDto(values),
  );
  return mapSafetyLog(data);
};

export const updateSafetyLog = async (values: UpdateSafetyLogFormValues): Promise<SafetyLog> => {
  const { data } = await putRequest<SafetyLogResponseDto, unknown>(
    API.VESSEL,
    `/safety-log/${values.id}`,
    mapUpdateSafetyLogDto(values),
  );
  return mapSafetyLog(data);
};

export const deleteSafetyLog = async (id: string): Promise<void> => {
  await deleteRequest(API.VESSEL, `/safety-log/${id}`);
};

export const signSafetyLog = async (id: string): Promise<SafetyLog> => {
  const { data } = await patchRequest<SafetyLogResponseDto, unknown>(
    API.VESSEL,
    `/safety-log/${id}/sign`,
    {},
  );
  return mapSafetyLog(data);
};

export const getSafetyLogPdf = async (id: string, timezone?: string): Promise<Blob> => {
  return getRequestBlob(API.VESSEL, `/safety-log/${id}/pdf`, { timezone });
};
